import { useFirestoreGeneral } from "../../firebase/useFirestore.js";
import { useContext, useEffect, useState, useMemo } from "react";
import { db, timestamp } from "../../firebase/config.js"
import StarToggle from "../../components/common/StarToggle.jsx";
import { isStarred, starredPatch } from "../../helpers/starred/starred.js";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { SavedIcon } from "../../StateManagment/SavedIcon.js";
import Location from "../../hooks/Location.js"
import useLogRecordOpen from "../../hooks/useLogRecordOpen"
import useSettings from "../../hooks/Settings.jsx";
import { Auth } from "../../StateManagment/Auth.js";
import { AdressNavigation } from "../../StateManagment/AdressNavigation";
import AdressDashboard from "../../components/adresses/dashboard/AdressDashboard";
import { MeasurementSignalsProvider } from "../../StateManagment/MeasurementSignalsContext";
import Data from "../../components/adresses/data/Data";
import Notes from "../../components/adresses/notes/Notes";
import Research from "../../components/adresses/Research";
import Tasks from "../../components/adresses/tasks/Tasks";
import SelectableGrid from "../../components/common/SelectableGrid";
import ContactMoments from "../../components/adresses/contactMoments/ContactMoments";
import Materialen from "../../components/adresses/materials/Materialen";
import HouseholdSubtasks from "../../components/adresses/subtasks/HouseholdSubtasks";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import getAdressDescription from "../../helpers/getAdressDescription";
import AdressStatusSelect from "../../components/adresses/AdressStatusSelect";
import PreviousTrajectLink from "../../components/adresses/PreviousTrajectLink";
import { statusDisplaysFrom } from "../../hooks/useStatusDisplay";
import usePermissions from "../../hooks/usePermissions";
import recordStatusChange from "../../helpers/statusHistory/recordStatusChange";
import NotesAttachments from "../../components/personas/reports/NotesAttachments";

const AdressDetails = () => {
     // Context
        const [saved, setSaved] = useContext(SavedIcon)
        const [adressNavigation, setAdressNavigation] = useContext(AdressNavigation)
        const [auth] = useContext(Auth)
        const { worksIn, has: hasPermission, ready: permissionsReady } = usePermissions()
        // Voor de ster in de kop: dezelfde lezing als ReadOnlyGuard, zonder diens
        // fieldset en melding — die horen niet in een paginakop.
        const canEditStar = !permissionsReady || hasPermission('addresses.edit')

        // State
        const [adressDocid, setAdressDocid] = useState('')
        const [adressTitle, setAdressTitle] = useState('')
        const [adressDescription, setAdressDescription] = useState('')
        
        // Hooks
        const adressId = Location()[3]
        // Auditlog: leggen vast dat dit dossier geopend is. Zie
        // docs/features/clientdossier-auditlog.md.
        useLogRecordOpen('household', adressId)
        // useSettings draait hier al; de wachttijd meeliften scheelt drie
        // listeners in MarkUnreachableButton, dat altijd gemount is.
        // `loaded` afdwingen: tot dat moment staat de wachttijd nog op de
        // standaard van 14 en zou de knop bij 30/60 te vroeg verschijnen.
        const { impactFunctionality, unreachableAfterDays, adressStatusConfig, loaded: settingsLoaded } = useSettings()

        // Tabbladen verbergen die niet bij het onderdeel van deze rol horen. Een
        // gordijn en geen slot: wie zo'n tabblad via een link opent, krijgt hem
        // gewoon. AI-CONTEXT: See docs/features/rol-onderdelen.md
        const adressTabs = useMemo(() => {
            const items = [
                { id: 'dashboard', label: 'Overzicht' },
                { id: 'data', label: 'Gegevens' },
                { id: 'notes', label: 'Notities' },
                { id: 'photos', label: "Foto's" },
            ]
            if (worksIn('planning')) {
                items.push({ id: 'tasks', label: 'Afspraken' })
                items.push({ id: 'subtasks', label: 'Subtaken' })
            }
            items.push({ id: 'contactMoments', label: 'Contactmomenten' })
            items.push({ id: 'materials', label: 'Materialen' })
            if (impactFunctionality && worksIn('impactMeten')) {
                items.push({ id: 'research', label: 'Onderzoeken' })
            }
            return items
        }, [impactFunctionality, worksIn])

        // Firestore
        const adress = useFirestoreGeneral('Adresses', 'ID', adressId || '')
        // Meeliften op de useSettings hierboven: een eigen useStatusDisplays()
        // zou een tweede stel listeners op hetzelfde document opzetten.
        const statusDisplay = useMemo(
            () => statusDisplaysFrom({ adressStatusConfig }).adress,
            [adressStatusConfig],
        )

        // Set docid
        useEffect(() => {
            if(adress){
                adress.map(item => (
                    setAdressDocid(item.docid),
                    setAdressTitle(item.Title)
                ))
            }
        }, [adress])

        // Resolve "Straatnaam Huisnummer, Woonplaats" for the page header.
        // Direct read (not a hook) — getAdressDescription scopes by CompagnyID.
        // The cancelled-guard keeps a slow response for a previous address from
        // overwriting the header after switching addresses.
        useEffect(() => {
            if(!adressId) return
            let cancelled = false
            getAdressDescription(adressId).then((description) => {
                if(!cancelled) setAdressDescription(description)
            })
            return () => { cancelled = true }
        }, [adressId])

        // Title Handler
        const titleHandler = async (e) => {
    
            const docid = e.target.dataset.docid
            const value = e.target.value
    
            await db.collection('Adresses')
            .doc(docid)
            .update({
                Title: value
            })
            .then(() => {
                setSaved('flex')
             })
    
        }
    
         // Status Handler
        const statusHandler = async (e) => {
    
            const docid = e.target.dataset.docid
            const adressID = e.target.dataset.id
            const previousStatus = e.target.dataset.status
            const value = e.target.options[e.target.selectedIndex].value
    
            await db.collection('Adresses')
            .doc(docid)
            .update({
                Status: value,
            })

            await recordStatusChange({
                entityType: 'adress',
                entityID: adressID,
                fromStatus: previousStatus,
                toStatus: value,
                changedBy: auth?.ID,
            })
    
            setSaved('flex')
    
        }
    
  return (
    // Eén ophaalactie voor deze pagina: de Overzicht-kaart en de chips in de
    // Onderzoeken-tab kijken allebei mee.
    <MeasurementSignalsProvider entityId={adressId} entityKind="adress" enabled={Boolean(adressId)}>
     <div className="main">
      <div className="main-container" >
        <div className='page-header'>
            <PersonOutlineOutlinedIcon/>
            <h1>{adressDescription || adressTitle || 'Adres'}</h1>
            {/* De ster 'extra aandacht', ook hier: wie het huishouden open heeft
                hoeft er niet voor terug naar de lijst. Pas als het document
                binnen is; een knop die 'uit' zegt terwijl we het niet weten
                zou een klik uitlokken die iets wegschrijft.
                AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading */}
            {Array.isArray(adress) && adress[0] && (
                <StarToggle
                    starred={isStarred(adress[0])}
                    disabled={!canEditStar}
                    onToggle={(next) => db.collection('Adresses').doc(adress[0].docid).update(starredPatch(next, auth?.ID, timestamp))}
                />
            )}
        </div>
            <div id='live-report-builder-content-container'>
                {adress && adress.map(item => (
                    <ReadOnlyGuard key={item.ID} edit="addresses.edit">
                        <div id='persona-personal-data-container'>
                            <input id='edit-persona-name-input' type="text" defaultValue={item.Title} data-docid={item.docid} onChange={titleHandler}/>
                            <div id='persona-status-container'>
                                <AdressStatusSelect value={item.Status} display={statusDisplay} docid={item.docid} id={item.ID} onChange={statusHandler} />
                            </div>
                        </div>
                    </ReadOnlyGuard>
                ))}
                {/* Buiten de ReadOnlyGuard: wie alleen mag lezen, mag ook zien dat er een eerder traject is. */}
                {adress && adress.map(item => (
                    <PreviousTrajectLink key={`traject-${item.ID}`} adress={item} />
                ))}
                <SelectableGrid
                    items={adressTabs}
                    getId={(item) => item.id}
                    getLabel={(item) => item.label}
                    selectedId={adressNavigation}
                    onClick={setAdressNavigation}
                />
                {adress && adress.map(item => (
                    <div key={item.ID}>
                            <AdressDashboard adressNavigation={adressNavigation}/>
                            <Data adressNavigation={adressNavigation} showSelfPickup/>
                            <Notes adressNavigation={adressNavigation}/>
                            {/* Same ReportFiles bijlagen the mobile household page writes to. */}
                            <div style={{ display: adressNavigation === 'photos' ? 'block' : 'none' }}>
                                <NotesAttachments
                                    parentId={adressId}
                                    parentType="adress"
                                    title="Foto's en documenten"
                                    editPermission="addresses.edit"
                                />
                            </div>
                            <Research adressNavigation={adressNavigation} item={item} adressDocid={adressDocid}/>
                            <Tasks adressNavigation={adressNavigation} adressTitle={adressTitle}/>
                            <HouseholdSubtasks adressNavigation={adressNavigation}/>
                            <ContactMoments adressNavigation={adressNavigation} adressDocid={item.docid} adressStatus={item.Status} unreachableAfterDays={settingsLoaded ? unreachableAfterDays : null}/>
                            <Materialen adressNavigation={adressNavigation}/>
                    </div>
                ))}
            </div>

        </div>
    </div>
    </MeasurementSignalsProvider>
  )
}

export default AdressDetails
